import React, { useState } from 'react';
import SiteNav from '../../components/site/SiteNav';
import SiteFooter from '../../components/site/SiteFooter';
import { Shield, FileText, Lock, Scale } from 'lucide-react';

const Legal = ({ defaultSection = 'privacy' }) => {
    const [activeSection, setActiveSection] = useState(defaultSection);

    const sections = [
        { id: 'privacy', title: 'Gizlilik Sözleşmesi', icon: Lock },
        { id: 'kvkk', title: 'KVKK Bilgilendirme', icon: Shield },
        { id: 'sales', title: 'Satış Sözleşmesi', icon: FileText },
        { id: 'terms', title: 'Kullanım Koşulları', icon: Scale },
    ];

    const content = {
        privacy: `
            <h2>Gizlilik Politikası</h2>
            <p class="legal-updated">Son güncelleme: 7 Temmuz 2026</p>
            <p>Ugra Teknoloji ("Ugra", "biz"), ugra.io ve app.ugra.io alan adları üzerinden sunduğu sadakat ve müşteri ilişkileri yönetimi (CRM) hizmetini ("Platform") kullanan işletmelerin, işletme yetkililerinin ve son kullanıcıların (müşterilerin) gizliliğine önem verir. Bu Gizlilik Politikası, Platform'u kullandığınızda hangi verileri hangi amaçla topladığımızı, nasıl koruduğumuzu ve haklarınızı açıklar. Kişisel verilerin işlenmesine ilişkin ayrıntılı hukuki bilgilendirme için <strong>KVKK Aydınlatma Metni</strong> bölümünü de inceleyiniz.</p>

            <h3>1. Kapsam ve Roller</h3>
            <p>Platform iki tür kullanıcıya hizmet verir ve verinin niteliğine göre rollerimiz değişir:</p>
            <ul>
                <li><strong>İşletmeler ve işletme yetkilileri:</strong> Hesap oluşturan işletmelere ait veriler bakımından Ugra <strong>veri sorumlusudur</strong>.</li>
                <li><strong>Son kullanıcılar (müşteriler):</strong> Bir işletmenin sadakat programına katılan müşterilere ait veriler bakımından ilgili <strong>işletme veri sorumlusu</strong>, Ugra ise işletme adına ve talimatıyla hizmet sunan <strong>veri işleyendir</strong>. Bu tür verilerle ilgili talepleriniz için öncelikle hizmet aldığınız işletmeye başvurmanız gerekir.</li>
            </ul>

            <h3>2. Topladığımız Veriler</h3>
            <ul>
                <li><strong>Hesap verileri:</strong> ad, soyad, e-posta, telefon, (işletmeler için) işletme adı, sektör, adres, vergi/iletişim bilgileri.</li>
                <li><strong>Kimlik doğrulama:</strong> şifreniz özetlenmiş (hash) biçimde saklanır; Ugra düz metin şifrenizi görmez.</li>
                <li><strong>Kullanım ve işlem verileri:</strong> sipariş kayıtları, sadakat puanları, ziyaret ve check-in geçmişi, kampanya/kupon kullanımı, tercihler ve (opsiyonel) doğum günü.</li>
                <li><strong>Teknik veriler:</strong> IP adresi, tarayıcı/cihaz bilgisi, oturum ve log kayıtları, hata kayıtları.</li>
                <li><strong>Ödeme verileri (yalnızca işletmeler):</strong> abonelik ve ödeme durumu. <strong>Kart bilgileriniz Ugra tarafından saklanmaz</strong>; ödemeler lisanslı ödeme kuruluşu üzerinden alınır.</li>
            </ul>

            <h3>3. Çerezler ve Yerel Depolama</h3>
            <p>Platform, oturumunuzu sürdürmek ve deneyimi iyileştirmek için tarayıcınızın yerel depolama (localStorage) alanını ve zorunlu çerezleri kullanır. Örneğin müşteri portalında oturumunuz ve sepetiniz cihazınızda saklanır. Tarayıcı ayarlarınızdan bu verileri temizleyebilirsiniz; ancak bu durumda bazı işlevler çalışmayabilir.</p>

            <h3>4. Verileri Kullanma Amaçlarımız</h3>
            <ul>
                <li>Hizmeti sunmak, hesabınızı oluşturmak ve yönetmek,</li>
                <li>Sadakat programını, siparişleri ve kampanyaları yürütmek,</li>
                <li>Güvenliği sağlamak, dolandırıcılığı ve kötüye kullanımı önlemek,</li>
                <li>Talep ettiğiniz bildirim ve e-postaları göndermek,</li>
                <li>Hizmet kalitesini ölçmek, sorunları gidermek ve geliştirmek,</li>
                <li>Yasal yükümlülüklerimizi yerine getirmek.</li>
            </ul>

            <h3>5. Hizmet Sağlayıcılar ve Üçüncü Taraflar</h3>
            <p>Hizmetin sağlanabilmesi için aşağıdaki altyapı ve hizmet sağlayıcılarla, yalnızca gerekli veriler ölçüsünde çalışırız:</p>
            <ul>
                <li><strong>Supabase</strong> — veritabanı, kimlik doğrulama ve dosya depolama,</li>
                <li><strong>Vercel</strong> — uygulama barındırma ve sunucusuz fonksiyonlar,</li>
                <li><strong>Upstash (Redis)</strong> — önbellek, oturum ve hız sınırlama,</li>
                <li><strong>Resend</strong> — e-posta / doğrulama kodu gönderimi,</li>
                <li><strong>iyzico</strong> — abonelik ve online ödeme işlemleri,</li>
                <li><strong>Google (Gemini)</strong> — yapay zeka destekli özellikler (yalnızca ilgili özellik kullanıldığında),</li>
                <li><strong>Sentry</strong> — hata izleme,</li>
                <li><strong>OneSignal</strong> — anlık bildirim (izin verdiyseniz).</li>
            </ul>
            <p>Bu sağlayıcıların bir kısmının sunucuları yurt dışında bulunabilir; bu durumda veri aktarımı KVKK hükümlerine uygun olarak gerçekleştirilir (bkz. KVKK Aydınlatma Metni).</p>

            <h3>6. Veri Güvenliği</h3>
            <p>Verileriniz aktarım sırasında TLS/HTTPS ile şifrelenir; erişim yetkilendirme, kiracı (tenant) bazlı izolasyon, şifre özetleme ve hız sınırlama gibi teknik ve idari tedbirlerle korunur. Buna rağmen internet üzerinden hiçbir aktarımın %100 güvenli olmadığını hatırlatırız.</p>

            <h3>7. Saklama Süresi</h3>
            <p>Kişisel verileriniz, hizmetin sunulduğu süre boyunca ve ilgili mevzuatta öngörülen zamanaşımı/saklama süreleri boyunca saklanır. Hesabınızın kapatılması veya talebiniz halinde, yasal saklama yükümlülükleri saklı kalmak kaydıyla verileriniz silinir veya anonim hale getirilir.</p>

            <h3>8. Haklarınız</h3>
            <p>KVKK md. 11 kapsamındaki haklarınızı KVKK Aydınlatma Metni'nde belirtilen yöntemlerle kullanabilirsiniz.</p>

            <h3>9. Çocukların Gizliliği</h3>
            <p>Platform 18 yaşından küçüklere yönelik değildir. 18 yaşından küçük olduğunu tespit ettiğimiz kişilere ait veriler talep üzerine veya resen silinir.</p>

            <h3>10. Değişiklikler ve İletişim</h3>
            <p>Bu politikayı zaman zaman güncelleyebiliriz; güncel sürüm bu sayfada yayımlanır. Sorularınız için: <a href="mailto:info@ugra.io">info@ugra.io</a>.</p>
        `,
        kvkk: `
            <h2>KVKK Aydınlatma Metni</h2>
            <p class="legal-updated">Son güncelleme: 7 Temmuz 2026</p>
            <p>İşbu Aydınlatma Metni; 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") md. 10 ve "Aydınlatma Yükümlülüğünün Yerine Getirilmesinde Uyulacak Usul ve Esaslar Hakkında Tebliğ" uyarınca, veri sorumlusu sıfatıyla Ugra tarafından hazırlanmıştır.</p>

            <h3>1. Veri Sorumlusu</h3>
            <p>
                <strong>Unvan:</strong> Ugra Teknoloji San. ve Tic. Ltd. Şti. ("Ugra")<br>
                <strong>Adres:</strong> [şirketin açık adresi]<br>
                <strong>MERSIS No:</strong> [MERSIS numarası]<br>
                <strong>KEP Adresi:</strong> [KEP adresi]<br>
                <strong>E-posta:</strong> <a href="mailto:info@ugra.io">info@ugra.io</a>
            </p>

            <h3>2. Ugra'nın Sıfatı</h3>
            <p>Ugra, işlediği veriye göre iki farklı sıfat taşır:</p>
            <ul>
                <li><strong>Veri Sorumlusu:</strong> İşletme hesabı sahiplerinin/yetkililerinin ve site ziyaretçilerinin verileri için.</li>
                <li><strong>Veri İşleyen:</strong> İşletmelerin kendi müşterilerine ait olup Platform'a kaydettiği veriler için. Bu verilerde <strong>veri sorumlusu ilgili işletmedir</strong>; Ugra yalnızca işletmenin talimatıyla veriyi işler.</li>
            </ul>

            <h3>3. İşlenen Kişisel Veri Kategorileri</h3>
            <ul>
                <li><strong>Kimlik:</strong> ad, soyad,</li>
                <li><strong>İletişim:</strong> e-posta, telefon, adres,</li>
                <li><strong>Müşteri İşlem:</strong> sipariş, sadakat puanı, ziyaret/check-in geçmişi, kampanya kullanımı,</li>
                <li><strong>İşlem Güvenliği:</strong> özetlenmiş şifre, IP, oturum ve log kayıtları,</li>
                <li><strong>Finans (işletmeler):</strong> abonelik ve ödeme kayıtları (kart verisi Ugra'da saklanmaz),</li>
                <li><strong>Pazarlama:</strong> bildirim tercihleri, (opsiyonel) doğum günü.</li>
            </ul>

            <h3>4. İşleme Amaçları</h3>
            <ul>
                <li>Üyelik ve hizmet sözleşmesinin kurulması ve ifası,</li>
                <li>Sadakat programı, sipariş ve kampanya süreçlerinin yürütülmesi,</li>
                <li>Bilgi güvenliği ve kötüye kullanımın önlenmesi,</li>
                <li>Talep ve şikayetlerin yönetilmesi, iletişim faaliyetleri,</li>
                <li>Hukuki yükümlülüklerin yerine getirilmesi ve hizmetin geliştirilmesi.</li>
            </ul>

            <h3>5. İşlemenin Hukuki Sebepleri (KVKK md. 5 ve 6)</h3>
            <ul>
                <li>Bir sözleşmenin kurulması veya ifasıyla doğrudan ilgili olması,</li>
                <li>Veri sorumlusunun hukuki yükümlülüğünü yerine getirmesi,</li>
                <li>İlgili kişinin temel hak ve özgürlüklerine zarar vermemek kaydıyla veri sorumlusunun meşru menfaati,</li>
                <li>Yukarıdaki şartlar bulunmadığında, ilgili kişinin <strong>açık rızası</strong> (ör. ticari elektronik ileti/bildirim ve doğum günü gibi opsiyonel veriler).</li>
            </ul>

            <h3>6. Toplama Yöntemi</h3>
            <p>Kişisel verileriniz; web ve mobil web formları, QR kod ile check-in, e-posta/OTP doğrulaması ve Platform'u kullanımınız sırasında elektronik ortamda otomatik ve kısmen otomatik yollarla toplanır.</p>

            <h3>7. Kişisel Verilerin Aktarımı</h3>
            <p>Verileriniz, hizmetin sunulması amacıyla ve ölçüsünde aşağıdaki alıcı gruplarına aktarılabilir:</p>
            <ul>
                <li>Bulut altyapı ve hizmet sağlayıcıları: <strong>Supabase</strong> (veritabanı/kimlik doğrulama), <strong>Vercel</strong> (barındırma), <strong>Upstash</strong> (önbellek), <strong>Resend</strong> (e-posta), <strong>Sentry</strong> (hata izleme), <strong>OneSignal</strong> (bildirim), <strong>Google/Gemini</strong> (yapay zeka özellikleri),</li>
                <li>Ödeme kuruluşu: <strong>iyzico</strong> (abonelik ödemeleri),</li>
                <li>Hizmet aldığınız işletme (son kullanıcı verileriniz bakımından),</li>
                <li>Yetkili kamu kurum ve kuruluşları (kanunen gerekli hallerde).</li>
            </ul>
            <p><strong>Yurt dışına aktarım:</strong> Bazı hizmet sağlayıcıların sunucuları yurt dışında bulunabilir. Bu aktarımlar KVKK md. 9 çerçevesinde, açık rızanıza veya Kanun'da öngörülen diğer şartlara dayanılarak gerçekleştirilir.</p>

            <h3>8. Saklama Süreleri</h3>
            <p>Veriler, işleme amacının gerektirdiği süre ve ilgili mevzuattaki zamanaşımı süreleri boyunca saklanır; sürenin sonunda silinir, yok edilir veya anonim hale getirilir.</p>

            <h3>9. İlgili Kişinin Hakları (KVKK md. 11)</h3>
            <p>Veri sahibi olarak;</p>
            <ul>
                <li>Kişisel verinizin işlenip işlenmediğini öğrenme,</li>
                <li>İşlenmişse buna ilişkin bilgi talep etme,</li>
                <li>İşlenme amacını ve amaca uygun kullanılıp kullanılmadığını öğrenme,</li>
                <li>Yurt içinde/dışında aktarıldığı üçüncü kişileri bilme,</li>
                <li>Eksik/yanlış işlenmişse düzeltilmesini isteme,</li>
                <li>KVKK md. 7 çerçevesinde silinmesini/yok edilmesini isteme,</li>
                <li>Düzeltme/silme işlemlerinin aktarıldığı üçüncü kişilere bildirilmesini isteme,</li>
                <li>Münhasıran otomatik sistemlerle analiz sonucu aleyhinize bir sonuç çıkmasına itiraz etme,</li>
                <li>Kanuna aykırı işleme nedeniyle zarara uğramanız halinde zararın giderilmesini talep etme</li>
            </ul>
            <p>haklarına sahipsiniz.</p>

            <h3>10. Başvuru Yöntemi</h3>
            <p>Haklarınıza ilişkin taleplerinizi, "Veri Sorumlusuna Başvuru Usul ve Esasları Hakkında Tebliğ" uyarınca <a href="mailto:info@ugra.io">info@ugra.io</a> adresine ya da yukarıdaki adrese yazılı olarak iletebilirsiniz. Talebiniz en geç 30 gün içinde ücretsiz olarak sonuçlandırılır (işlemin maliyet gerektirmesi halinde Kurul tarifesi uygulanabilir).</p>

            <h3>11. Güncellemeler</h3>
            <p>Bu metin gerektiğinde güncellenir; güncel sürüm bu sayfada yayımlanır.</p>
        `,
        sales: `
            <h2>Mesafeli Satış Sözleşmesi</h2>
            <p class="legal-updated">Son güncelleme: 7 Temmuz 2026</p>
            <p>İşbu Mesafeli Satış Sözleşmesi ("Sözleşme"), 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği uyarınca, aşağıda bilgileri yer alan taraflar arasında elektronik ortamda kurulmuştur.</p>

            <h3>1. Taraflar</h3>
            <p>
                <strong>Satıcı:</strong> Ugra Teknoloji San. ve Tic. Ltd. Şti. — [adres] — <a href="mailto:info@ugra.io">info@ugra.io</a><br>
                <strong>Alıcı (Abone):</strong> Platform'a kayıt olan ve aboneliği satın alan işletme/kullanıcı.
            </p>

            <h3>2. Sözleşmenin Konusu</h3>
            <p>Sözleşme'nin konusu, Ugra tarafından "yazılım hizmeti" (SaaS) olarak sunulan CRM ve sadakat sistemi abonelik paketinin, Alıcı tarafından elektronik ortamda satın alınmasına ilişkin tarafların hak ve yükümlülüklerinin belirlenmesidir.</p>

            <h3>3. Hizmet ve Ücret</h3>
            <ul>
                <li><strong>Ücretsiz (Free) Plan:</strong> Yalnızca menü yönetimi ve sınırlı özellikleri içerir; ücret alınmaz.</li>
                <li><strong>Pro Plan:</strong> Tüm özellikleri içeren aylık abonelik. Güncel ücret ve varsa vergiler, satın alma ekranında açıkça gösterilir.</li>
            </ul>
            <p>Fiyatlar Satıcı tarafından güncellenebilir; güncel fiyat, satın alma anında geçerli olan fiyattır.</p>

            <h3>4. Ödeme</h3>
            <p>Ödemeler, lisanslı ödeme kuruluşu <strong>iyzico</strong> altyapısı üzerinden kredi/banka kartı ile alınır. Abonelik, ilgili dönem sonunda tarafların aksini bildirmemesi halinde aynı koşullarla yenilenebilir. Kart bilgileriniz Satıcı tarafından saklanmaz.</p>

            <h3>5. Hizmetin İfası</h3>
            <p>Hizmet dijitaldir ve ödemenin onaylanmasının ardından <strong>anında</strong> elektronik olarak sunulmaya başlanır. Ayrı bir fiziksel teslimat yoktur.</p>

            <h3>6. Cayma Hakkı</h3>
            <p>Mesafeli Sözleşmeler Yönetmeliği md. 15/1-(ğ) uyarınca, <strong>elektronik ortamda anında ifa edilen ve gayrimaddi teslim edilen dijital içerik/hizmetlerde cayma hakkı bulunmamaktadır</strong>. Alıcı, hizmetin ifasına başlanmasını onaylayarak cayma hakkının sona ereceğini kabul eder. Buna rağmen abonelik, aşağıdaki madde uyarınca her zaman sonraki dönem için iptal edilebilir.</p>

            <h3>7. İptal ve Yenileme</h3>
            <p>Alıcı, aboneliğini dilediği zaman iptal edebilir. İptal, içinde bulunulan ödenmiş dönemin sonunda geçerli olur; kalan dönem için kural olarak iade yapılmaz. İptal sonrası hesap, ücretsiz plan koşullarına dönebilir.</p>

            <h3>8. Fatura</h3>
            <p>Satıcı, satın alınan hizmete ilişkin faturayı yürürlükteki mevzuata uygun olarak düzenler ve Alıcı'ya elektronik ortamda iletir.</p>

            <h3>9. Fikri Mülkiyet</h3>
            <p>Platform yazılımı, tasarımı ve markası Ugra'ya aittir. Abonelik, Alıcı'ya yalnızca hizmeti kullanma lisansı verir; devir, kopyalama veya tersine mühendislik hakkı vermez.</p>

            <h3>10. Uyuşmazlıkların Çözümü</h3>
            <p>Alıcı'nın tüketici sıfatını haiz olması halinde, Ticaret Bakanlığı'nca ilan edilen parasal sınırlar dahilinde Tüketici Hakem Heyetleri ve Tüketici Mahkemeleri yetkilidir. Diğer hallerde Türk Hukuku uygulanır ve [şehir] mahkemeleri ile icra daireleri yetkilidir.</p>

            <h3>11. Yürürlük</h3>
            <p>Alıcı, satın alma adımında bu Sözleşme'yi onayladığında Sözleşme elektronik ortamda kurulmuş ve yürürlüğe girmiş sayılır.</p>
        `,
        terms: `
            <h2>Kullanım Koşulları</h2>
            <p class="legal-updated">Son güncelleme: 7 Temmuz 2026</p>
            <p>Ugra Platform'unu kullanarak bu Kullanım Koşulları'nı kabul etmiş sayılırsınız. Kabul etmiyorsanız Platform'u kullanmamalısınız.</p>

            <h3>1. Tanımlar</h3>
            <p><strong>Platform:</strong> ugra.io / app.ugra.io üzerinden sunulan hizmetler. <strong>İşletme:</strong> hesap oluşturan ticari kullanıcı. <strong>Son Kullanıcı:</strong> bir işletmenin sadakat programına katılan müşteri.</p>

            <h3>2. Hesap ve Güvenlik</h3>
            <p>Kayıt sırasında doğru ve güncel bilgi vermeyi, hesap güvenliğinizi (şifre vb.) korumayı ve hesabınız altında gerçekleşen işlemlerden sorumlu olduğunuzu kabul edersiniz. Yetkisiz kullanım halinde derhal bize bildirmelisiniz.</p>

            <h3>3. İşletmenin Veri Sorumlusu Yükümlülüğü</h3>
            <p>İşletme, kendi müşterilerine ait kişisel verileri toplarken ve Platform'a işlerken KVKK ve ilgili mevzuata uymayı; gerekli aydınlatma ve açık rızaları almayı taahhüt eder. Bu verilerde <strong>veri sorumlusu işletmedir</strong>; Ugra veri işleyen sıfatıyla altyapı ve teknik hizmet sağlar.</p>

            <h3>4. Kullanım Kuralları ve Yasaklı Fiiller</h3>
            <ul>
                <li>Platform'u hukuka aykırı, aldatıcı veya üçüncü kişilerin haklarını ihlal edici şekilde kullanmak,</li>
                <li>Sistemi tersine mühendislik yapmak, kaynak koda erişmeye çalışmak veya güvenliği aşmak,</li>
                <li>Zararlı yazılım yaymak, otomatik/aşırı istekle sistemi yormak,</li>
                <li>İzinsiz ticari elektronik ileti (spam) göndermek,</li>
                <li>Başka bir kullanıcının hesabına yetkisiz erişmek</li>
            </ul>
            <p>yasaktır ve hesabın askıya alınması/kapatılmasına yol açabilir.</p>

            <h3>5. Fikri Mülkiyet</h3>
            <p>Platform'a ilişkin tüm yazılım, tasarım, marka ve içerik hakları Ugra'ya aittir. İşletmenin kendi yüklediği menü, görsel ve müşteri içerikleri ise işletmeye aittir; işletme bunların hukuka uygunluğundan sorumludur.</p>

            <h3>6. Hizmet Sürekliliği ve Değişiklikler</h3>
            <p>Hizmeti kesintisiz sunmaya çalışırız; ancak bakım, güncelleme veya mücbir sebeplerle geçici kesintiler olabilir. Özellikleri geliştirebilir, değiştirebilir veya durdurabiliriz; önemli değişiklikleri makul ölçüde bildiririz.</p>

            <h3>7. Sorumluluğun Sınırlandırılması</h3>
            <p>Platform "olduğu gibi" sunulur. Yürürlükteki hukukun izin verdiği azami ölçüde Ugra; dolaylı, arızi veya sonuç niteliğindeki zararlardan ve işletmenin kendi müşteri verilerini mevzuata aykırı işlemesinden doğan sonuçlardan sorumlu değildir.</p>

            <h3>8. Fesih ve Askıya Alma</h3>
            <p>Bu koşulların ihlali halinde hesabınızı askıya alabilir veya kapatabiliriz. Siz de hesabınızı dilediğiniz zaman kapatabilirsiniz. Fesih sonrası veriler, Gizlilik Politikası ve mevzuat uyarınca işlenir.</p>

            <h3>9. Uygulanacak Hukuk ve Yetki</h3>
            <p>Bu koşullara Türk Hukuku uygulanır. Uyuşmazlıklarda, tüketici mevzuatının yetkili kıldığı merciler saklı kalmak kaydıyla [şehir] mahkemeleri ve icra daireleri yetkilidir.</p>

            <h3>10. İletişim</h3>
            <p>Sorularınız için: <a href="mailto:info@ugra.io">info@ugra.io</a></p>
        `
    };

    return (
        <div style={{ background: 'var(--bg-main)', minHeight: '100vh' }}>
            <SiteNav />
            <section style={{ padding: '10rem 10% 4rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '4rem', alignItems: 'flex-start' }}>
                    <aside style={{ position: 'sticky', top: '120px' }}>
                        <div className="card glass" style={{ padding: '1rem' }}>
                            {sections.map(section => (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: 'none',
                                        background: activeSection === section.id ? 'var(--primary)' : 'transparent',
                                        color: activeSection === section.id ? 'white' : 'var(--text-main)',
                                        fontWeight: 600,
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <section.icon size={20} /> {section.title}
                                </button>
                            ))}
                        </div>
                    </aside>

                    <main className="card glass legal-main" style={{ padding: '4rem', minHeight: '600px' }}>
                        <div className="legal-content" dangerouslySetInnerHTML={{ __html: content[activeSection] }}></div>
                    </main>
                </div>
            </section>
            <SiteFooter />
            <style>{`
                .legal-content h2 { font-size: 2.25rem; margin-bottom: 0.5rem; font-weight: 900; letter-spacing: -1px; color: var(--text-main); }
                .legal-content h3 { font-size: 1.2rem; margin-top: 2.25rem; margin-bottom: 0.75rem; font-weight: 800; color: var(--primary); }
                .legal-content p { color: var(--text-muted); line-height: 1.8; margin-bottom: 1rem; }
                .legal-content ul { color: var(--text-muted); line-height: 1.8; margin: 0 0 1.25rem 0.5rem; padding-left: 1.25rem; }
                .legal-content li { margin-bottom: 0.4rem; }
                .legal-content strong { color: var(--text-main); font-weight: 700; }
                .legal-content a { color: var(--primary); font-weight: 600; text-decoration: none; }
                .legal-content a:hover { text-decoration: underline; }
                .legal-content .legal-updated { font-size: 0.85rem; font-weight: 600; color: var(--text-muted); margin-bottom: 2rem; opacity: 0.8; }
                @media (max-width: 1024px) {
                    section > div { grid-template-columns: 1fr !important; }
                    aside { position: static !important; margin-bottom: 2rem; }
                }
                @media (max-width: 640px) {
                    section { padding: 8rem 5% 3rem !important; }
                    .legal-main { padding: 1.5rem !important; }
                    .legal-content h2 { font-size: 1.75rem; }
                }
            `}</style>
        </div>
    );
};

export default Legal;
